import { useCallback, useReducer, useRef } from 'react'
import { useAuthContext } from '@/auth/AuthProvider'
import { chatReducer, INITIAL_STATE, type ChatState, type StreamEvent } from './streamReducer'
import { foundryTransport } from './foundryTransport'
import type { ChatTransport } from './transport'

/** About three frames: smooth enough to read as streaming, and caps markdown parses at 20 a second. */
const DELTA_FLUSH_MS = 50

export interface UseChatResult extends ChatState {
  send: (message: string) => Promise<void>
  newThread: () => void
  /** Start a fresh thread with this as its first message. */
  sendInNewThread: (message: string) => Promise<void>
  cancel: () => void
  /** Epoch ms of the current thread's first message; null before one is sent. */
  threadStartedAt: number | null
}

/**
 * Chat hook. Owns the reducer and the in-flight abort handle — everything
 * model-agnostic. The *transport* owns talking to the model and turning its
 * wire protocol into `StreamEvent`s. Defaults to `foundryTransport`; pass a
 * different `ChatTransport` (e.g. a future `claudeTransport`) to swap the
 * backend with no change to the reducer or the render layer.
 */
export function useChat(transport: ChatTransport = foundryTransport): UseChatResult {
  const { authState, getAuthHeaders } = useAuthContext()
  const [state, dispatch] = useReducer(chatReducer, INITIAL_STATE)
  const abortRef = useRef<AbortController | null>(null)
  // When the current thread's first message was sent, for the thread label.
  // A ref (not reducer state) because it isn't part of the conversation the
  // model sees — and because this hook is mounted above the router, it
  // survives navigating away from /chat and back, which a ref inside
  // ChatView would not.
  const startedAtRef = useRef<number | null>(null)

  // Conversation state is in-memory only. A page refresh starts a fresh
  // chain (no `previous_response_id`), which also forces Foundry to
  // refetch the MCP tool list — handy when we've just changed tool
  // names/descriptions on the server. Earlier we persisted `threadId`
  // to localStorage, which caused the messages/threadId asymmetry that
  // led to the hallucination bug (server-side context survived, UI
  // didn't). Removing the persistence is the cleaner fix.

  // Drive a transport's event stream into the reducer under a fresh
  // AbortController so `cancel()` / `newThread()` can interrupt it. The
  // transport yields `stream.error` for its own failures; the catch here
  // is a backstop for unexpected throws.
  const runStream = useCallback(
    async (makeEvents: (signal: AbortSignal) => AsyncIterable<StreamEvent>): Promise<void> => {
      const abort = new AbortController()
      abortRef.current = abort
      // Text arrives a token at a time, and every update re-parses the whole
      // answer's markdown. Deltas are merged and applied at most every
      // DELTA_FLUSH_MS; any other event flushes them first, so the reducer
      // still sees events in order. After an abort the buffer is dropped,
      // as the deltas after it always were.
      // Declared with `as` so TypeScript does not narrow them to null: flush()
      // assigns them from a closure, which control-flow analysis cannot see.
      let pending = null as Extract<StreamEvent, { kind: 'thread.message.delta' }> | null
      let timer = null as ReturnType<typeof setTimeout> | null
      const flush = (): void => {
        if (timer !== null) {
          clearTimeout(timer)
          timer = null
        }
        const event = pending
        pending = null
        if (event !== null && !abort.signal.aborted) dispatch(event)
      }
      try {
        for await (const event of makeEvents(abort.signal)) {
          if (abort.signal.aborted) break
          if (event.kind === 'thread.message.delta') {
            if (pending !== null && pending.messageId !== event.messageId) flush()
            pending =
              pending === null
                ? event
                : { ...pending, textDelta: pending.textDelta + event.textDelta }
            timer ??= setTimeout(flush, DELTA_FLUSH_MS)
            continue
          }
          flush()
          dispatch(event)
        }
        flush()
      } catch (err) {
        flush()
        if (!(err instanceof Error && err.name === 'AbortError')) {
          dispatch({
            kind: 'stream.error',
            message: err instanceof Error ? err.message : 'Stream failed',
          })
        }
      } finally {
        if (timer !== null) clearTimeout(timer)
        abortRef.current = null
      }
    },
    [],
  )

  const sendOn = useCallback(
    async (
      message: string,
      history: ChatState['messages'],
      threadId: string | null,
    ): Promise<void> => {
      if (authState.status !== 'authenticated') return

      // Render the user message immediately so the UI doesn't wait on
      // the first SSE frame to show what the user just typed.
      const localId = `local-${Date.now()}`
      dispatch({ kind: 'user.message.appended', messageId: localId, text: message })
      startedAtRef.current ??= Date.now()

      await runStream(signal =>
        transport.startTurn(message, { getAuthHeaders, history, threadId, signal }),
      )
    },
    [authState, getAuthHeaders, transport, runStream],
  )

  const send = useCallback(
    (message: string): Promise<void> => sendOn(message, state.messages, state.threadId),
    [sendOn, state.messages, state.threadId],
  )

  const newThread = useCallback(() => {
    abortRef.current?.abort()
    startedAtRef.current = null
    dispatch({ kind: 'thread.reset' })
  }, [])

  // `newThread()` then `send()` would chain onto the old thread: `send`
  // still holds the threadId from before the reset.
  const sendInNewThread = useCallback(
    (message: string): Promise<void> => {
      newThread()
      return sendOn(message, [], null)
    },
    [newThread, sendOn],
  )

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    // Surfacing the cancel as an error keeps UX honest — the assistant
    // bubble stops mid-stream and the run-status returns to idle on next send.
    dispatch({ kind: 'stream.error', message: 'Cancelled' })
  }, [])

  return {
    ...state,
    send,
    newThread,
    sendInNewThread,
    cancel,
    threadStartedAt: startedAtRef.current,
  }
}
